import { ref } from 'vue';

export type UseFileDragDetectorOptions = {
  /**
   * Can be used for the duration of the closing animation in milliseconds.
   * (wait until drop targets closed, then unmount them)
   */
  dragLeaveDelay?: number;
};

/**
 * Detects when a file is dragged over a container element.
 * Does not act a as a drop target, but you can use it to let drop targets
 * appear when a file is dragged over the container.
 */
export function useFileDragDetection(options: UseFileDragDetectorOptions = {}) {
  const { dragLeaveDelay = 300 } = options;

  const isFileDragOver = ref(false);
  const isInDragLeaveDelay = ref(false);
  const dragOverCounter = ref(0);

  function onFileDragEnter(event: DragEvent) {
    event.preventDefault();

    // Only handle if actual files are being dragged, not internal draggable elements
    if (!event.dataTransfer?.types.includes('Files')) {
      return;
    }

    dragOverCounter.value += 1;
    isInDragLeaveDelay.value = false;
    isFileDragOver.value = true;
  }

  function onFileDragLeave(event: DragEvent) {
    event.preventDefault();

    // Don't check for Files here - dataTransfer might be inaccessible when leaving window
    // We only want to skip if we never started tracking (counter is 0)
    if (dragOverCounter.value === 0) {
      return;
    }

    dragOverCounter.value -= 1;

    if (dragOverCounter.value <= 0) {
      dragOverCounter.value = 0;
      startDragLeaveDelay();
    }
  }

  function onFileDragEnd(event: DragEvent) {
    event.preventDefault();

    // Don't check for Files here either - just reset if we were tracking
    if (dragOverCounter.value === 0 && !isFileDragOver.value) {
      return;
    }

    dragOverCounter.value = 0;
    startDragLeaveDelay();
  }

  function startDragLeaveDelay() {
    isInDragLeaveDelay.value = true;
    // Wait for the shrink animation to complete before unmounting
    setTimeout(() => {
      isFileDragOver.value = false;
      isInDragLeaveDelay.value = false;
    }, dragLeaveDelay);
  }

  return {
    isFileDragOver,
    isInDragLeaveDelay,
    dragOverCounter,

    // Event handlers (to be bound to the container element)
    handlers: {
      onDragenter: onFileDragEnter,
      onDragleave: onFileDragLeave,
      onDragend: onFileDragEnd,
      onDrop: onFileDragEnd,
      onDragover: (event: DragEvent) => event.preventDefault(),
    },
  };
}
